import React from 'react';
import moment from 'moment';
import { DOC_TYPES, DOC_LABELS } from '../../../../services/installerDocumentService';

const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const DOC_ROWS = [
  { type: DOC_TYPES.W9, label: DOC_LABELS[DOC_TYPES.W9], hint: 'Required by dealers before they can pay you.' },
  { type: DOC_TYPES.COI, label: DOC_LABELS[DOC_TYPES.COI], hint: 'Proof of liability coverage. Re-upload when your policy renews.' }
];

/**
 * One document row — W-9 or COI. Presence, upload date, and the actions.
 *
 * No URL is held anywhere: View asks the parent to fetch a fresh short-lived signed URL
 * at click time. These files are private (a W-9 carries a TIN/SSN), so nothing about
 * them is cached in the DOM or in state.
 */
const DocumentRow = ({ docType, label, hint, doc, busy, canManage, onUpload, onRemove, onView }) => {
  const inputId = `document-upload-${docType}`;

  return (
    <div className="d-flex align-items-center justify-content-between border-bottom py-3">
      <div style={{ minWidth: 0 }}>
        <div className="font-weight-bold">
          {label}
          {doc ? (
            <span className="badge badge-success ml-2">On file</span>
          ) : (
            <span className="badge badge-light border ml-2 text-muted">Not uploaded</span>
          )}
        </div>
        <small className="text-muted d-block">
          {doc?.uploaded_at
            ? `Uploaded ${moment(doc.uploaded_at).format('MMM D, YYYY')}`
            : hint}
        </small>
      </div>

      <div className="d-flex align-items-center flex-shrink-0 ml-3">
        {doc && (
          <button
            className="btn btn-outline-secondary btn-sm mr-2"
            onClick={() => onView(docType)}
            disabled={busy}
          >
            <i className="fa-sharp fa-regular fa-eye mr-1"></i>
            View
          </button>
        )}

        {canManage && (
          <>
            <label
              className="btn btn-outline-primary btn-sm mb-0 mr-2"
              htmlFor={inputId}
              style={{ cursor: busy ? 'default' : 'pointer' }}
            >
              <i className="fa-sharp fa-regular fa-upload mr-1"></i>
              {busy ? 'Working...' : (doc ? 'Replace' : 'Upload')}
              <input
                id={inputId}
                type="file"
                accept="application/pdf"
                onChange={(e) => onUpload(docType, e)}
                disabled={busy}
                style={{ display: 'none' }}
              />
            </label>

            {doc && (
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => onRemove(docType)}
                disabled={busy}
                title={`Remove ${label}`}
              >
                <i className="fa-sharp fa-regular fa-trash"></i>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const BusinessTab = ({ installer, logoUrl, logoUploading, logoRemoving, onLogoUpload, onLogoRemove, onOpenSettingsModal, onOpenWaiverModal, getTimeTrackingLabel, documents = {}, documentBusy = {}, canManageDocuments = false, onDocumentUpload, onDocumentRemove, onDocumentView }) => {
  const waiverPreview = stripHtml(installer?.waiver_html);
  const waiverUpdatedAt = installer?.waiver_updated_at;

  return (
    <>
      {/* Company Information */}
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">Company Information</h4>
        </div>
        <div className="card-body">
          {installer && (
            <div className="row">
              <div className="col-md-4 mb-3">
                <strong>Business Name:</strong><br />
                {installer.Installer_Name || 'Not set'}
              </div>
              <div className="col-md-4 mb-3">
                <strong>Email:</strong><br />
                {installer.installer_email || installer.Email || 'Not set'}
              </div>
              <div className="col-md-4 mb-3">
                <strong>Phone:</strong><br />
                {installer.installer_phone_number || 'Not set'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company Logo */}
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-image mr-2"></i>
            Company Logo
          </h4>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">
            Upload your company logo to replace the default Jobvious logo in the sidebar navigation.
          </p>
          <div className="d-flex align-items-center">
            {logoUrl ? (
              <div className="mr-4">
                <div style={{ background: '#f26c35', borderRadius: '8px', padding: '12px 16px', display: 'inline-block' }}>
                  <img
                    src={logoUrl}
                    alt="Company Logo"
                    style={{ maxWidth: '200px', maxHeight: '60px', objectFit: 'contain' }}
                  />
                </div>
              </div>
            ) : (
              <div className="mr-4 text-muted" style={{ minWidth: '200px' }}>
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                  <i className="fa-sharp fa-regular fa-image fa-2x mb-2 d-block" style={{ opacity: 0.4 }}></i>
                  <small>No logo uploaded</small>
                </div>
              </div>
            )}
            <div>
              <label className="btn btn-outline-primary btn-sm mb-2" style={{ cursor: 'pointer' }}>
                <i className="fa-sharp fa-regular fa-upload mr-1"></i>
                {logoUploading ? 'Uploading...' : (logoUrl ? 'Change Logo' : 'Upload Logo')}
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/svg+xml"
                  onChange={onLogoUpload}
                  disabled={logoUploading || logoRemoving}
                  style={{ display: 'none' }}
                />
              </label>
              {logoUrl && (
                <div>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={onLogoRemove}
                    disabled={logoUploading || logoRemoving}
                  >
                    <i className="fa-sharp fa-regular fa-trash mr-1"></i>
                    {logoRemoving ? 'Removing...' : 'Remove Logo'}
                  </button>
                </div>
              )}
              <small className="text-muted d-block mt-2">PNG, JPEG, or SVG. Max 2MB.</small>
            </div>
          </div>
        </div>
      </div>

      {/* Company Settings */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Company Settings</h4>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={onOpenSettingsModal}
          >
            <i className="fa-sharp fa-regular fa-edit mr-1"></i> Edit
          </button>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Materials Tracking:</strong><br />
              <span className={`badge ${installer?.materials_tracking_enabled !== false ? 'badge-success' : 'badge-secondary'}`}>
                {installer?.materials_tracking_enabled !== false ? 'Enabled' : 'Disabled'}
              </span>
              <p className="text-muted small mt-1 mb-0">
                {installer?.materials_tracking_enabled !== false
                  ? 'Technicians will be prompted to record materials used when finishing services'
                  : 'Materials collection is disabled'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Time Tracking:</strong><br />
              <span className={`badge ${(installer?.time_tracking_mode && installer.time_tracking_mode !== 'none') ? 'badge-success' : 'badge-secondary'}`}>
                {getTimeTrackingLabel(installer?.time_tracking_mode)}
              </span>
              <p className="text-muted small mt-1 mb-0">
                {installer?.time_tracking_mode === 'required'
                  ? 'Staff must check in before starting work on jobs'
                  : installer?.time_tracking_mode === 'voluntary'
                  ? 'Staff can optionally check in/out to track hours'
                  : 'Time tracking features are hidden'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Cash Discount:</strong><br />
              <span className={`badge ${installer?.waive_platform_fee_offline ? 'badge-success' : 'badge-secondary'}`}>
                {installer?.waive_platform_fee_offline ? 'Enabled' : 'Disabled'}
              </span>
              <p className="text-muted small mt-1 mb-0">
                5% discount on catalog prices for cash/check payments
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Service Waiver */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-file-signature mr-2"></i>
            Customer Work Agreement
          </h4>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={onOpenWaiverModal}
          >
            <i className="fa-sharp fa-regular fa-edit mr-1"></i>
            {waiverPreview ? 'Edit' : 'Create'}
          </button>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">
            Shown to customers on the signature step at retail check-in. Replaces the default agreement language.
          </p>
          {waiverPreview ? (
            <>
              <div
                className="border rounded p-3 mb-2 bg-light text-truncate"
                style={{ maxWidth: '100%' }}
                title={waiverPreview}
              >
                {waiverPreview.length > 200 ? `${waiverPreview.slice(0, 200)}…` : waiverPreview}
              </div>
              {waiverUpdatedAt && (
                <small className="text-muted">
                  Last updated {moment(waiverUpdatedAt).format('MMM D, YYYY [at] h:mm A')}
                </small>
              )}
            </>
          ) : (
            <div className="text-muted">
              <em>Not configured — default agreement language will be shown at check-in.</em>
            </div>
          )}
        </div>
      </div>

      {/* Compliance Documents — W-9 and Certificate of Insurance */}
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-file-pdf mr-2"></i>
            Compliance Documents
          </h4>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-3">
            Dealers you work with can retrieve these from their Financials page, so they
            don't have to request them by email. PDF only, up to 10MB.
          </p>
          {DOC_ROWS.map(({ type, label, hint }) => (
            <DocumentRow
              key={type}
              docType={type}
              label={label}
              hint={hint}
              doc={documents[type]}
              busy={!!documentBusy[type]}
              canManage={canManageDocuments}
              onUpload={onDocumentUpload}
              onRemove={onDocumentRemove}
              onView={onDocumentView}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default BusinessTab;
